/**
 * Animated retro "puzzle" banner for Ward Rush (/game).
 * Self-contained: no images, no fonts, no libs — the letters sit on
 * bouncing puzzle tiles, over an arcade-dark background with scanlines
 * and twinkling pixels. Honors prefers-reduced-motion.
 */

// Site palette: Fae Blush, Dusk Rose, Night Sage, Soft Ash (on Moonbone)
const TILE_COLORS = [
  "#d8b6b8", // fae blush
  "#b48a93", // dusk rose
  "#8e9a92", // night sage
  "#b9ada3", // soft ash
];

const WORDS = ["WARD", "RUSH"];

const CSS = `
@keyframes wrBob {
  0%   { transform: translateY(0); }
  50%  { transform: translateY(-7px); }
  100% { transform: translateY(0); }
}
@keyframes wrBlink {
  0%, 55% { opacity: 1; }
  56%, 100% { opacity: 0; }
}
@keyframes wrTwinkle {
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.9; }
}
@keyframes wrMarch {
  from { background-position: 0 0; }
  to   { background-position: 28px 0; }
}
.wr-tile { animation: wrBob 1.6s steps(4, end) infinite; }
.wr-blink { animation: wrBlink 1.1s step-end infinite; }
.wr-star { animation: wrTwinkle 2.4s steps(3, end) infinite; }
.wr-marquee { animation: wrMarch 0.9s steps(7) infinite; }
@media (prefers-reduced-motion: reduce) {
  .wr-tile, .wr-blink, .wr-star, .wr-marquee { animation: none; }
}
`;

const STARS = [
  { top: "14%", left: "6%", d: "0s" },
  { top: "70%", left: "12%", d: "0.7s" },
  { top: "24%", left: "88%", d: "0.3s" },
  { top: "64%", left: "93%", d: "1.1s" },
  { top: "12%", left: "72%", d: "1.6s" },
  { top: "78%", left: "55%", d: "0.9s" },
  { top: "18%", left: "38%", d: "1.3s" },
];

export default function GameBanner() {
  let tileIndex = 0;
  return (
    <a
      href="/game/"
      aria-label="Play Ward Rush — a cozy hospital time-management game"
      className="group block md:col-span-2 rounded-2xl overflow-hidden border transition-all duration-200
        hover:-translate-y-1 hover:shadow-xl
        border-[#d8b6b8]/60 shadow-sm hover:border-[#b48a93]"
    >
      <style>{CSS}</style>

      {/* marching marquee edge */}
      <div
        className="wr-marquee p-[5px]"
        style={{
          background:
            "repeating-linear-gradient(90deg,#b48a93 0 14px,#d8b6b8 14px 28px)",
        }}
      >
        <div
          className="relative px-4 py-7 sm:py-8 text-center overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 140% at 50% 0%, #f7f3ee 0%, #f2eee9 55%, #e9e2da 100%)",
          }}
        >
          {/* scanlines */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background:
                "repeating-linear-gradient(0deg, rgba(185,173,163,0.18) 0 1px, transparent 1px 4px)",
            }}
          />
          {/* twinkling pixels */}
          {STARS.map((s, i) => (
            <span
              key={i}
              aria-hidden
              className="wr-star absolute w-1.5 h-1.5 rounded-full"
              style={{ top: s.top, left: s.left, animationDelay: s.d, background: "#b48a93" }}
            />
          ))}

          {/* puzzle-tile title */}
          <div className="relative flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
            {WORDS.map((word) => (
              <span key={word} className="inline-flex gap-1.5">
                {word.split("").map((ch, i) => {
                  const color = TILE_COLORS[tileIndex++ % TILE_COLORS.length];
                  return (
                    <span
                      key={i}
                      className="wr-tile inline-flex items-center justify-center
                        w-9 h-9 sm:w-12 sm:h-12 rounded-md font-mono font-extrabold
                        text-xl sm:text-3xl select-none"
                      style={{
                        background: color,
                        color: "#f2eee9",
                        boxShadow:
                          "inset -3px -4px 0 rgba(87,72,82,0.22), inset 3px 3px 0 rgba(255,255,255,0.35), 0 4px 0 rgba(87,72,82,0.28)",
                        textShadow: "2px 2px 0 rgba(87,72,82,0.35)",
                        animationDelay: `${tileIndex * 0.13}s`,
                      }}
                    >
                      {ch}
                    </span>
                  );
                })}
              </span>
            ))}
          </div>

          <p className="relative text-xs sm:text-sm font-medium tracking-wide mb-3" style={{ color: "#8a7880" }}>
            🩺 A cozy hospital time-management game — treat patients, beat the
            clock, top the leaderboard!
          </p>

          <span
            className="wr-blink relative inline-block font-mono font-bold tracking-[0.2em] text-sm sm:text-base"
            style={{ color: "#7d8f85" }}
          >
            ▶ PLAY NOW
          </span>
        </div>
      </div>
    </a>
  );
}
